// Which APNG the anvil character (Codey) plays. The PNGs live in this
// package's assets/anvil-animations; consumers turn the file name from
// `anvilAnimationFile` into a URL for wherever they serve that folder.

// The seeded `active` pool: the hand-drawn set plus programmatic variants
// generated by scripts/generate-anvil-variants.mjs (eureka, on-fire, orbiting,
// sleuthing, swimming, zen). `caffeinating` is generated too but deliberately
// absent here: it is bound to waiting on sub-agents, so drawing it at random
// would drain the signal.
export const ANIMATION_VARIANTS = [
  "beautiful-mind",
  "concentrate",
  "considering",
  "doomscrolling",
  "eureka",
  "on-fire",
  "orbiting",
  "pong",
  "pondering",
  "ruminating",
  "running",
  "sleuthing",
  "swimming",
  "thinking",
  "zen",
] as const;

export type AnvilActiveVariant = (typeof ANIMATION_VARIANTS)[number];

/**
 * - `active`: seeded pick from {@link ANIMATION_VARIANTS} (right after user input)
 * - `type`: typing while prose or file content streams in
 * - `caffeinating`: coffee break while sub-agents carry the turn
 * - `sipping`: the drink itself, an occasional beat inside `caffeinating`
 * - `idle`: subdued loop while tools or text are visibly active
 * - `ruminating`: personality loop while thinking streams and in quiet gaps
 */
export type AnvilAnimationVariant =
  | "active"
  | "type"
  | "idle"
  | "ruminating"
  | "caffeinating"
  | "sipping";

export type AnvilTheme = "light" | "dark";

/** The one PNG each non-seeded variant maps to. */
const VARIANT_ASSETS: Record<
  Exclude<AnvilAnimationVariant, "active">,
  string
> = {
  type: "type",
  idle: "idle",
  ruminating: "ruminating",
  caffeinating: "caffeinating-hold",
  sipping: "caffeinating-sip",
};

/** FNV-1a hash of `seed` folded into `[0, modulo)`. */
export function seededIndex(seed: string, modulo: number): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % modulo;
}

/**
 * Seed-stable pick from the `active` PNG set. Every surface that renders the
 * anvil character for the same turn (transcript indicator, scroll-to-bottom
 * pill) resolves through this so they all show the same animation.
 */
export function seededAnvilActiveVariant(seed: string): AnvilActiveVariant {
  return ANIMATION_VARIANTS[
    seededIndex(`${seed}:variant`, ANIMATION_VARIANTS.length)
  ]!;
}

/**
 * File name of an anvil animation, e.g. `("active", "thinking", "dark")` →
 * `anvil-thinking-dark.png`. `activeVariant` is only consulted when
 * `animationVariant` is `active`; the other variants map to a single PNG each.
 */
export function anvilAnimationFile(
  animationVariant: AnvilAnimationVariant,
  activeVariant: AnvilActiveVariant,
  theme: AnvilTheme,
): string {
  const variantName =
    animationVariant === "active"
      ? activeVariant
      : VARIANT_ASSETS[animationVariant];
  return `anvil-${variantName}-${theme}.png`;
}

/** Every PNG the working indicator can reach, for prefetch. */
export const ANVIL_ANIMATION_ASSETS = Array.from(
  new Set([...ANIMATION_VARIANTS, ...Object.values(VARIANT_ASSETS)]),
);
